import { useCallback, useEffect, useState } from "react";
import { Package, MapPin, Plus, CheckCircle2, Truck, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

/* Ghost-Pak — the courier app. Board of escrowed manifests, your active runs with
   checkpoint scanning, and the send-a-pak form. Undeclared freight pays double and
   sweats at every port-authority scan. */

const PAYLOADS = [
  ["documents", "Documents", 180], ["electronics", "Electronics", 320],
  ["medical", "Medical cold-chain", 420], ["fragile", "Fragile freight", 500],
  ["undeclared", "Undeclared (no questions)", 950],
];

const DEMO = {
  posted: [
    { id: "gp1", payload_class: "fragile", payload_label: "Fragile freight", origin: "Docks Bay 4", destination: "Northside Lockups", payout: 500, shipper: "Ada K.", checkpoints: [], status: "posted" },
    { id: "gp2", payload_class: "undeclared", payload_label: "Undeclared", origin: "Pale Row", destination: "Route 9 Weighbridge", payout: 950, shipper: "anon", checkpoints: [], status: "posted" },
  ],
  mine: [
    { id: "gp3", payload_class: "electronics", payload_label: "Electronics", origin: "Foundry Avenue", destination: "Mile End Yard", payout: 320, status: "in_transit", courier: "You", checkpoints: [{ label: "Route 9 Weighbridge", at: "" }] },
  ],
};

export default function GhostPakApp({ demo, serverId, token, accent }) {
  const [tab, setTab] = useState("board"); // board | runs | send
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({ payload_class: "documents", origin: "", destination: "" });
  const [cpLabel, setCpLabel] = useState({});

  const load = useCallback(async () => {
    if (demo) { setData(DEMO); return; }
    try {
      const r = await api.get(`/nc/servers/${serverId}/ghostpak/board`, { params: { token } });
      setData(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [demo, serverId, token]);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const act = async (key, fn) => {
    setBusy(key);
    try {
      const r = await fn();
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const create = () => act("create", () => api.post(`/nc/servers/${serverId}/ghostpak/manifests`, { token, ...form }));
  const accept = (id) => act(id, () => api.post(`/nc/servers/${serverId}/ghostpak/manifests/${id}/accept`, { token }));
  const checkpoint = (id) => act(id + "cp", () => api.post(`/nc/servers/${serverId}/ghostpak/manifests/${id}/checkpoint`, { token, label: cpLabel[id] || "" }));
  const deliver = (id) => act(id + "d", () => api.post(`/nc/servers/${serverId}/ghostpak/manifests/${id}/deliver`, { token }));

  if (!data) return <div className="p-6 font-mono text-[10px] text-zinc-500 uppercase tracking-[0.25em] animate-pulse">Opening the board…</div>;

  const tabBtn = (id, label) => (
    <button key={id} data-testid={`gpak-tab-${id}`} onClick={() => setTab(id)}
      className={`flex-1 py-2 font-mono text-[9px] font-bold uppercase tracking-[0.2em] border-b-2 transition-colors ${tab === id ? "text-zinc-50" : "text-zinc-500 border-transparent"}`}
      style={tab === id ? { borderBottomColor: accent } : {}}>{label}</button>
  );

  return (
    <div data-testid="ghostpak-app" className="flex flex-col h-full">
      <div className="flex border-b border-white/10">{tabBtn("board", "Board")}{tabBtn("runs", "My Runs")}{tabBtn("send", "Send a Pak")}</div>
      <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2" style={{ scrollbarWidth: "thin" }}>

        {tab === "board" && (
          <>
            <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-600 flex items-center gap-1.5">
              <Truck className="w-3 h-3" /> escrowed manifests · payout releases on delivery
            </p>
            {data.posted.length === 0 && <p className="font-mono text-[9px] text-zinc-600 border border-dashed border-white/10 p-4 text-center">Board's clear — check back soon</p>}
            {data.posted.map((m) => (
              <div key={m.id} data-testid={`gpak-posted-${m.id}`} className="border border-white/10 bg-black/40 p-3">
                <div className="flex items-center gap-2">
                  <Package className="w-3.5 h-3.5 shrink-0" style={{ color: accent }} />
                  <span className="font-mono text-[10px] text-zinc-100 font-bold">{m.payload_label}</span>
                  <span className="ml-auto font-mono text-[10px] font-bold text-emerald-400">${m.payout.toLocaleString()}</span>
                </div>
                <p className="font-mono text-[8px] text-zinc-500 mt-1 flex items-center gap-1"><MapPin className="w-2.5 h-2.5" /> {m.origin} → {m.destination}</p>
                {m.payload_class === "undeclared" && (
                  <p className="font-mono text-[7px] text-amber-500/80 mt-1 flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5" /> scans at every checkpoint — 12% seize risk</p>
                )}
                <button data-testid={`gpak-accept-${m.id}`} onClick={() => accept(m.id)} disabled={!!busy}
                  className="mt-2 w-full font-mono text-[9px] font-bold uppercase tracking-[0.2em] py-2 text-black disabled:opacity-40 transition-opacity hover:opacity-85"
                  style={{ backgroundColor: accent }}>Take the job</button>
              </div>
            ))}
          </>
        )}

        {tab === "runs" && (
          <>
            {data.mine.length === 0 && <p className="font-mono text-[9px] text-zinc-600 border border-dashed border-white/10 p-4 text-center">Nothing on your docket</p>}
            {data.mine.map((m) => (
              <div key={m.id} data-testid={`gpak-run-${m.id}`} className="border border-white/10 bg-black/40 p-3">
                <div className="flex items-center gap-2">
                  <Package className="w-3.5 h-3.5 shrink-0" style={{ color: accent }} />
                  <span className="font-mono text-[10px] text-zinc-100 font-bold">{m.payload_label}</span>
                  <span className={`ml-auto font-mono text-[8px] uppercase tracking-[0.15em] px-1.5 py-0.5 border ${m.status === "delivered" ? "border-emerald-500/40 text-emerald-400" : m.status === "seized" ? "border-red-500/40 text-red-400" : "border-white/20 text-zinc-400"}`}>{m.status.replace("_", " ")}</span>
                </div>
                <p className="font-mono text-[8px] text-zinc-500 mt-1">{m.origin} → {m.destination} · ${m.payout.toLocaleString()}</p>
                <p className="font-mono text-[8px] text-zinc-600 mt-0.5">checkpoints: {m.checkpoints.length}/2{m.checkpoints.map((c) => ` · ${c.label}`)}</p>
                {m.status === "in_transit" && m.courier_pc !== undefined && (
                  <div className="mt-2 flex flex-col gap-1.5">
                    <div className="flex gap-1.5">
                      <input data-testid={`gpak-cp-${m.id}`} value={cpLabel[m.id] || ""} onChange={(e) => setCpLabel((s) => ({ ...s, [m.id]: e.target.value }))}
                        placeholder="Checkpoint name" className="flex-1 bg-black/60 border border-white/15 px-2 py-1.5 font-mono text-[9px] text-zinc-100 outline-none focus:border-white/40" />
                      <button data-testid={`gpak-scan-${m.id}`} onClick={() => checkpoint(m.id)} disabled={!!busy || !(cpLabel[m.id] || "").trim()}
                        className="font-mono text-[8px] font-bold uppercase tracking-[0.15em] border border-white/20 text-zinc-300 px-2.5 hover:border-white/50 transition-colors disabled:opacity-40">Scan</button>
                    </div>
                    <button data-testid={`gpak-deliver-${m.id}`} onClick={() => deliver(m.id)} disabled={!!busy || m.checkpoints.length < 2}
                      className="font-mono text-[9px] font-bold uppercase tracking-[0.2em] py-2 text-black disabled:opacity-40 transition-opacity hover:opacity-85"
                      style={{ backgroundColor: "#4ade80" }}>
                      <CheckCircle2 className="w-3 h-3 inline mr-1" /> Deliver & release ${m.payout.toLocaleString()}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {tab === "send" && (
          <div className="border border-white/10 bg-black/40 p-3 flex flex-col gap-2">
            <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-600">post a manifest — payout escrowed up front</p>
            <select data-testid="gpak-payload" value={form.payload_class} onChange={(e) => setForm({ ...form, payload_class: e.target.value })}
              className="bg-black/60 border border-white/15 px-2 py-2 font-mono text-[10px] text-zinc-100 outline-none [color-scheme:dark]">
              {PAYLOADS.map(([id, label, price]) => <option key={id} value={id}>{label} — ${price}</option>)}
            </select>
            <input data-testid="gpak-origin" value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })}
              placeholder="Pickup — e.g. Docks Bay 4" className="bg-black/60 border border-white/15 px-2 py-2 font-mono text-[10px] text-zinc-100 outline-none focus:border-white/40" />
            <input data-testid="gpak-destination" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })}
              placeholder="Drop-off — e.g. Mile End Yard" className="bg-black/60 border border-white/15 px-2 py-2 font-mono text-[10px] text-zinc-100 outline-none focus:border-white/40" />
            <button data-testid="gpak-post" onClick={create} disabled={!!busy || form.origin.trim().length < 2 || form.destination.trim().length < 2}
              className="font-mono text-[9px] font-bold uppercase tracking-[0.2em] py-2.5 text-black disabled:opacity-40 transition-opacity hover:opacity-85 flex items-center justify-center gap-1.5"
              style={{ backgroundColor: accent }}>
              <Plus className="w-3 h-3" /> Post manifest — ${PAYLOADS.find(([id]) => id === form.payload_class)?.[2]} escrow
            </button>
          </div>
        )}

        <button data-testid="gpak-refresh" onClick={load} className="self-center font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-600 hover:text-zinc-300 flex items-center gap-1 mt-1 transition-colors">
          <RefreshCw className="w-2.5 h-2.5" /> refresh
        </button>
      </div>
    </div>
  );
}
